import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  AddCircleOutline as AddIcon,
  Replay as ReplayIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { emailToName } from '../utils/emailToName';
import { useNavigate } from 'react-router-dom';
import { useAdminService, ScenarioSummary } from '../services/adminService';
import PageHeader from '../components/PageHeader';

const sectionBoxStyle = {
  borderRadius: 2,
  p: 3,
  mb: 3,
};

const labelStyle = {
  fontWeight: 600,
};

const sortOptions = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'az', label: 'A-Z (Name)' },
  { value: 'za', label: 'Z-A (Name)' },
];

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { fetchAllScenarioDetails } = useAdminService();

  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [sort, setSort] = useState<string>('newest');

  function handleBack(): void {
    navigate('/');
  }

  // Fetch scenarios
  const fetchData = () => {
    setLoading(true);
    setError(null);
    fetchAllScenarioDetails()
      .then(setScenarios)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Sorting logic
  const sortedScenarios = [...scenarios].sort((a, b) => {
    if (sort === 'newest') {
      return new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime();
    } else if (sort === 'oldest') {
      return new Date(a.createdAt ?? '').getTime() - new Date(b.createdAt ?? '').getTime();
    } else if (sort === 'az') {
      return (a.title ?? '').localeCompare(b.title ?? '');
    } else if (sort === 'za') {
      return (b.title ?? '').localeCompare(a.title ?? '');
    }
    return new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime();
  });

  const handleSortChange = (event: SelectChangeEvent) => {
    setSort(event.target.value);
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      py: 8,
      p: 4,
      height: '100vh',
      overflowY: 'auto'
    }}>
      <Box sx={{
        width: { xs: '95%', md: '95%', lg: '95%', xl: '85%' },
        p: 4,
        borderRadius: 3,
      }}>
        {/* Header Row */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          mb: 4
        }}>
          <PageHeader
            title="Roleplay Management"
            subtitle="View, edit, assign, or create new roleplays below."
            onBack={handleBack}
            showBackButton={true}
          />
          {/* Sort Dropdown aligned right and bottom */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            height: '100%'
          }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="sort-label">Sort By</InputLabel>
              <Select
                labelId="sort-label"
                value={sort}
                label="Sort By"
                onChange={handleSortChange}
              >
                {sortOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr', xl: '1fr 1fr 1fr' },
            gap: 3,

          }}
        >
          <Card sx={{ ...sectionBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardContent>
              <Typography variant="h6" sx={labelStyle} gutterBottom>
                Create New Scenario
              </Typography>
              <Typography variant="body2">
                Start a new roleplay scenario for your team.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size="large"
                sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
                  color: 'white'
                }}
                onClick={() => navigate('/admin/assessments/create')}
              >
                Create Scenario
              </Button>
            </CardActions>
          </Card>
          <Card sx={{ ...sectionBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardContent>
              <Typography variant="h6" sx={labelStyle} gutterBottom>
                Master Assignment List
              </Typography>
              <Typography variant="body2">
                Displays all assignments for easy management
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size="large"
                sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
                  color: 'white'
                }}
                onClick={() => navigate('/admin/assignments')}
              >
                View Assigments
              </Button>
            </CardActions>
          </Card>
          <Card sx={{ ...sectionBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardContent>
              <Typography variant="h6" sx={labelStyle} gutterBottom>
                Prompt Structures
              </Typography>
              <Typography variant="body2">
                Create and manage prompt structures for consistent roleplay scenarios
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size="large"
                sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
                  color: 'white'
                }}
                onClick={() => navigate('/admin/prompt-structures')}
              >
                Prompt Structures
              </Button>
            </CardActions>
          </Card>
        </Box>
          <div></div>

        {/* Roleplay Header */}
        <Box sx={{ mb: 3, backgroundColor: 'transparent'}}>
          <Typography variant="h4" sx={labelStyle} gutterBottom>
            Roleplays
          </Typography>
        </Box>

        {/* Loading/Error with Retry */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={fetchData}
            >
              Retry
            </Button>
          </Box>
        )}

        {/* Scenario Cards Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr', xl: '1fr 1fr 1fr' },
            gap: 3,
          }}
        >
          {!loading && !error && sortedScenarios.map((scenario) => (
            <Card
              key={scenario.id}
              sx={{
                ...sectionBoxStyle,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                position: 'relative',
                boxShadow: 2,
                overflow: 'visible',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                  transition: 'all 0.2s ease-in-out',
                }
              }}
              onClick={() => navigate(`/admin/assessments/${encodeURIComponent(scenario.id ?? '')}`)}
            >
              {/* <Chip
                label={scenario.active ? 'Active' : 'Inactive'}
                color={scenario.active ? 'success' : 'default'}
                sx={{
                  position: 'absolute',
                  top: -18,
                  right: 16,
                  zIndex: 2,
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: '12px',
                  background: scenario.active
                    ? 'linear-gradient(90deg, #43e97b 0%, #189e2eff 90%)'
                    : 'linear-gradient(90deg, rgba(255,0,192,1) 0%, rgba(255,109,21,1) 90%)',
                  color: 'white',
                  boxShadow: 2,
                  pointerEvents: 'none',
                  letterSpacing: 1
                }}
              /> */}
              <CardContent>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'left',
                  mb: 1
                }}>
                  <Typography variant="h6" sx={labelStyle} gutterBottom>
                    {scenario.title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    WebkitLineClamp: 5,
                  }}
                >
                  {scenario.description}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  {scenario.createdBy && `Created by: ${emailToName(scenario.createdBy)}`}
                </Typography>
                <Chip
                  icon={<TimeIcon sx={{ fontSize: 18 }} />}
                  label={`Created: ${scenario.createdAt ? new Date(scenario.createdAt).toLocaleDateString() : 'N/A'}`}
                  color="default"
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip
                  icon={<TimeIcon sx={{ fontSize: 18 }} />}
                  label={`Duration: ${scenario.duration ?? 'N/A'}`}
                  color="default"
                  size="small"
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPage;